import { ImportElementType, ImportTreeNode } from '../../models/classic-import.model';

/**
 * Auswahl im Classic-Import: Der Nutzer wählt Elemente direkt (`manual`), ihre Abhängigkeiten kommen transitiv dazu.
 * Dieselbe Hülle bildet das BE in `ImportSelection.closure`.
 */
export type TreeIndex = Map<string, ImportTreeNode>;
export type FolderState = 'all' | 'some' | 'none';

export interface TreeFolder {
  key: string;
  name: string;
  depth: number;
  folders: TreeFolder[];
  items: ImportTreeNode[];
  /** Alle Elemente in diesem Ordner und darunter. */
  keys: string[];
}

export type TreeRow =
  | { kind: 'folder'; key: string; name: string; depth: number; folder: TreeFolder }
  | { kind: 'item'; key: string; name: string; depth: number; node: ImportTreeNode };

export interface TreeFilter {
  text: string;
  type: ImportElementType | null;
}

const collator = new Intl.Collator('de');

export function indexTree(tree: ImportTreeNode[]): TreeIndex {
  return new Map(tree.map((n) => [n.sourceKey, n]));
}

export function closure(keys: Iterable<string>, index: TreeIndex): Set<string> {
  const out = new Set<string>();
  const todo: string[] = [];
  for (const key of keys) {
    if (index.has(key) && !out.has(key)) {
      out.add(key);
      todo.push(key);
    }
  }
  while (todo.length > 0) {
    for (const dep of index.get(todo.pop()!)!.dependsOn) {
      if (index.has(dep) && !out.has(dep)) {
        out.add(dep);
        todo.push(dep);
      }
    }
  }
  return out;
}

export function autoSelected(manual: Set<string>, index: TreeIndex): Set<string> {
  const effective = closure(manual, index);
  for (const key of manual) effective.delete(key);
  return effective;
}

/** Je automatisch gewähltem Element die direkt gewählten, die es brauchen. */
export function requirers(manual: Set<string>, index: TreeIndex): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const key of manual) {
    if (!index.has(key)) continue;
    for (const dep of closure([key], index)) {
      if (manual.has(dep)) continue;
      const list = out.get(dep);
      if (list) list.push(key);
      else out.set(dep, [key]);
    }
  }
  return out;
}

/** Abwählen geht nur bei direkt Gewähltem; automatisch Benötigtes bleibt, solange es gebraucht wird. */
export function toggleElement(manual: Set<string>, key: string, index: TreeIndex): Set<string> {
  if (manual.has(key)) {
    const next = new Set(manual);
    next.delete(key);
    return next;
  }
  if (closure(manual, index).has(key)) return manual;
  return new Set(manual).add(key);
}

/** Ordner: Ist alles darunter gewählt, fallen die direkt gewählten weg; sonst kommt alles darunter dazu. */
export function toggleKeys(manual: Set<string>, keys: string[], index: TreeIndex): Set<string> {
  const next = new Set(manual);
  if (folderState(keys, closure(manual, index)) === 'all') {
    for (const key of keys) next.delete(key);
  } else {
    for (const key of keys) next.add(key);
  }
  return next;
}

export function folderState(keys: string[], effective: Set<string>): FolderState {
  let selected = 0;
  for (const key of keys) if (effective.has(key)) selected++;
  if (selected === 0) return 'none';
  return selected === keys.length ? 'all' : 'some';
}

/** `null` heißt „die ganze Datei“ (`UpdateProjectImportRequest`). */
export function toSelection(manual: Set<string>, index: TreeIndex): string[] | null {
  const keys = [...index.keys()].filter((k) => manual.has(k));
  return keys.length === index.size && keys.length > 0 ? null : keys;
}

export function buildFolderTree(tree: ImportTreeNode[]): TreeFolder {
  const root = folder('', '', -1);
  const byKey = new Map<string, TreeFolder>();
  for (const n of tree) {
    let parent = root;
    let key = '';
    for (const name of n.folder) {
      key = key ? `${key}/${name}` : name;
      let child = byKey.get(key);
      if (!child) {
        child = folder(key, name, parent.depth + 1);
        byKey.set(key, child);
        parent.folders.push(child);
      }
      parent = child;
    }
    parent.items.push(n);
  }
  sortAndCollect(root);
  return root;
}

export function treeRows(root: TreeFolder, expanded: Set<string>, filter: TreeFilter): TreeRow[] {
  const text = filter.text.trim().toLowerCase();
  const filtering = text.length > 0 || filter.type !== null;
  const matches = (n: ImportTreeNode) =>
    (filter.type === null || n.type === filter.type) && (text === '' || n.name.toLowerCase().includes(text));
  const out: TreeRow[] = [];

  const walk = (f: TreeFolder): void => {
    for (const sub of f.folders) {
      const row: TreeRow = { kind: 'folder', key: sub.key, name: sub.name, depth: sub.depth, folder: sub };
      if (!filtering) {
        out.push(row);
        if (expanded.has(sub.key)) walk(sub);
        continue;
      }
      const at = out.length;
      walk(sub);
      if (out.length > at) out.splice(at, 0, row);
    }
    for (const n of f.items) {
      if (!filtering || matches(n)) {
        out.push({ kind: 'item', key: n.sourceKey, name: n.name, depth: f.depth + 1, node: n });
      }
    }
  };
  walk(root);
  return out;
}

function folder(key: string, name: string, depth: number): TreeFolder {
  return { key, name, depth, folders: [], items: [], keys: [] };
}

function sortAndCollect(f: TreeFolder): string[] {
  f.folders.sort((a, b) => collator.compare(a.name, b.name));
  f.items.sort((a, b) => collator.compare(a.name, b.name));
  for (const sub of f.folders) for (const key of sortAndCollect(sub)) f.keys.push(key);
  for (const n of f.items) f.keys.push(n.sourceKey);
  return f.keys;
}
